import {
  Formik as BaseFormik,
  useFormik as useBaseFormik,
  type FormikConfig,
  type FormikProps,
  type FormikValues,
} from 'formik';

import { FormErrorToast, useFormErrorToast } from './FormErrorToast';

/**
 * Project-local replacement for formik's `<Formik>`.
 *
 * Identical API, but automatically renders a {@link FormErrorToast} so a submit
 * blocked by validation always gives the user feedback. Import this instead of
 * importing `Formik` directly from 'formik'.
 */
export function Formik<Values extends FormikValues = FormikValues>(props: FormikConfig<Values>) {
  const { children, ...rest } = props;
  return (
    <BaseFormik<Values> {...rest}>
      {(formik) => (
        <>
          <FormErrorToast />
          {typeof children === 'function' ? children(formik) : children}
        </>
      )}
    </BaseFormik>
  );
}

/**
 * Project-local replacement for formik's `useFormik`.
 *
 * Identical API, but wires up {@link useFormErrorToast} so a submit blocked by
 * validation always gives the user feedback. Import this instead of importing
 * `useFormik` directly from 'formik'.
 */
export function useFormik<Values extends FormikValues = FormikValues>(
  config: FormikConfig<Values>,
): FormikProps<Values> {
  const formik = useBaseFormik<Values>(config);
  useFormErrorToast(formik.submitCount, formik.isValid, formik.isValidating);
  return formik;
}
